import { computed, Ref, ref, shallowRef, ShallowRef } from "vue";
import axios from 'axios';

import { endpoint } from "@/remote/utils";
import {
  IssueMarker,
  MaintenanceKind,
  MaintenanceMarker,
  PreemptiveMaintenanceMarker
} from '@/components/MaintenanceMap/types';

export const monthMs = 1000 /*ms*/ * 60 /*s*/ * 60 /*min*/ * 24 /*h*/ * 30 /*d*/;
export const yearMs = monthMs * 12;

export function compare_markers(a: MaintenanceMarker, b: MaintenanceMarker): number {
  return Date.parse(a.date) - Date.parse(b.date);
}

export function compare_issues(a: IssueMarker, b: IssueMarker): number {
  if (a.priority !== b.priority) {
    return a.priority - b.priority;
  }
  return Date.parse(b.date) - Date.parse(a.date);
}

const preemptive_colors: Array<{ text: 'Blue' | 'Green' | 'Orange' | 'Red', color: string }> = [
  { text: "Blue", color: "#0373a4" },
  { text: "Green", color: "#00ff00" },
  { text: "Orange", color: "#ff8d3f" },
  { text: "Red", color: "#ff0000" }
] as const;


export function get_preemptive_color(color: string): string {
  const col = preemptive_colors.find(e => e.text === color);
  if (col) {
    return col.color;
  }
  return "#000000";
}

function issue_marker_color(priority: number): string {
  if (priority === 1) {
    return get_preemptive_color("Red");
  }
  if (priority === 2) {
    return get_preemptive_color("Orange");
  }
  return get_preemptive_color("Green");
}

function preemptive_marker_color(next_maint_date: string): string {
  const diff = Date.now() - Date.parse(next_maint_date);
  if (diff - 6 * monthMs > 0) {
    return get_preemptive_color("Red");
  }
  if (diff - 2 * monthMs > 0) {
    return get_preemptive_color("Orange");
  }
  if (diff + 4 * monthMs > 0) {
    return get_preemptive_color("Green");
  }
  return get_preemptive_color("Blue");
}

type IssueType = {
  id: number,
  park: {
    id: number,
    name: string,
    agency: number | null,
    address: string,
    city: string,
    postal_code: string,
  },
  name: string,
  kind: MaintenanceKind,
  made_by: number,
  issue_descr: string,
  start_date: string,
  is_solved: boolean,
  title: string,
  priority: number,
  last_curative_date: string,
  how_to_solve_descr: string,
}

type ParkType = {
  id: number,
  name: string,
  latitude: string,
  longitude: string,
  agency: number | null,
  address: string,
  city: string,
  cabinet_position: string | null,
  cabinet_key: boolean | null,
  installation_date: string,
  contract_maintenance_amount: number,
  latest_preventive_maintenance_date: string | null,
  contract_type: string,
  number_of_cs: number,
  how_approach_client: string,
  contract_validity_from: string,
  contract_validity_to: string,
  self_renewable: boolean,
  next_maint_date: string
}

type ContractInfo = {
  contract: boolean,
  contract_signed: boolean,
  contract_interrupted: boolean,
  maintenance_amount_cent: number,
  contract_type: string,
  validity_from: string | null,
  validity_to: string,
  self_renewable: boolean,
}

type MapParkRow = {
  id: number,
  name: string,
  latitude: string,
  longitude: string,
  agency: number | null,
  address: string,
  city: string,
  installation_date: string | null,
  latest_preventive_maintenance_date: string | null,
  contract_info: ContractInfo | null,
  electrical_cabinet_where: string | null,
  electrical_cabinet_with_key: boolean | null,
  how_approach_client: string,
  number_of_cs: number,
}

type MaintainedParkRow = MapParkRow & {
  installation_date: string,
  contract_info: ContractInfo & { validity_from: string },
}

type AgencyType = {
  id: number,
  name: string,
}

const NOT_ASSIGNED_AGENCY: AgencyType = { id: -1, name: 'Not assigned' };
const UNKNOWN_AGENCY: AgencyType = { id: -2, name: 'Unknown' };

function is_maintained_park(row: MapParkRow): row is MaintainedParkRow {
  const contract = row.contract_info;
  return row.installation_date !== null
    && contract !== null
    && contract.contract
    && contract.contract_signed
    && !contract.contract_interrupted
    && contract.maintenance_amount_cent > 0
    && contract.validity_from !== null;
}

function next_maintenance_date(contract_validity_from: string, latest_preventive_maintenance_date: string | null): string {
  const contractStartDate = new Date(contract_validity_from);

  if (latest_preventive_maintenance_date === null) {
    // No maintenance history: use one year after contract start
    const oneYearAfterContractStart = new Date(contractStartDate);
    oneYearAfterContractStart.setFullYear(oneYearAfterContractStart.getFullYear() + 1);
    return oneYearAfterContractStart.toISOString().split('T')[0];
  }

  const lastMaintenanceDate = new Date(latest_preventive_maintenance_date);
  // expected date is day and month of the contract start date
  const expectedMaintenanceDate = new Date(contractStartDate);
  expectedMaintenanceDate.setFullYear(lastMaintenanceDate.getFullYear());
  // With the first year available so that the date is AFTER the latest maintenance
  if (lastMaintenanceDate.getTime() - expectedMaintenanceDate.getTime() > 0) {
    expectedMaintenanceDate.setFullYear(lastMaintenanceDate.getFullYear() + 1);
  }

  // if lastMaint is too much nearby expected date, move the expected date to next year
  if (expectedMaintenanceDate.getTime() - lastMaintenanceDate.getTime() < 4 * monthMs) {
    expectedMaintenanceDate.setFullYear(expectedMaintenanceDate.getFullYear() + 1);
  }

  return expectedMaintenanceDate.toISOString().split('T')[0];
}

function to_maintained_park(row: MaintainedParkRow): ParkType {
  const contract = row.contract_info;
  return {
    id: row.id,
    name: row.name,
    latitude: row.latitude,
    longitude: row.longitude,
    address: row.address,
    city: row.city,
    agency: row.agency,
    installation_date: row.installation_date,
    latest_preventive_maintenance_date: row.latest_preventive_maintenance_date,
    contract_maintenance_amount: contract.maintenance_amount_cent / 100,
    contract_type: contract.contract_type,
    cabinet_position: row.electrical_cabinet_where,
    cabinet_key: row.electrical_cabinet_with_key,
    how_approach_client: row.how_approach_client,
    number_of_cs: row.number_of_cs,
    contract_validity_from: contract.validity_from,
    contract_validity_to: contract.validity_to,
    self_renewable: contract.self_renewable,
    next_maint_date: next_maintenance_date(contract.validity_from, row.latest_preventive_maintenance_date)
  };
}

async function fetchMapParks(): Promise<Array<MapParkRow>> {
  const response = await axios.get(endpoint('internal/current/map/parks/'), { withCredentials: true });
  return response.data;
}

async function fetchAgencies(): Promise<Array<AgencyType>> {
  const response = await axios.get(endpoint('internal/current/agency/'), { withCredentials: true });
  return response.data;
}

async function fetchUnsolvedTickets(): Promise<Array<IssueType>> {
  const out: Array<IssueType> = [];
  let url: string | null | undefined = endpoint('internal/current/tickets/?is_solved=0');
  while (url) {
    const response = await axios.get(url, { withCredentials: true });
    out.push(...response.data.results);
    url = response.data.next;
  }
  return out;
}

export function useMaintenanceMap() {
  const show_preemptive = ref(true);
  const show_issues = ref(true);
  const map_park_rows: ShallowRef<Array<MapParkRow>> = shallowRef([]);
  const agency_list: ShallowRef<Array<AgencyType>> = shallowRef([]);
  const unsolved_tickets: ShallowRef<Array<IssueType>> = shallowRef([]);
  const loading_data: Ref<boolean> = ref(false);
  const filtered_prio: Ref<Array<number>> = ref([]);
  const filtered_agencies: Ref<Array<number>> = ref([]);
  const filtered_preemptives: Ref<Array<string>> = ref([
    get_preemptive_color("Red"),
    get_preemptive_color("Orange"),
    get_preemptive_color("Green")
  ]);
  const filtered_parks: Ref<Array<number>> = ref([]);

  const priorities: Array<{ value: number, text: 'High' | 'Medium' | 'Low', color: string }> = [
    { value: 1, text: 'High', color: 'red' },
    { value: 2, text: 'Medium', color: 'orange' },
    { value: 3, text: 'Low', color: 'green' }
  ] as const;

  const map_parks_by_id = computed(() => new Map(map_park_rows.value.map(row => [row.id, row])));

  // tengo tutti i parchi la cui prossima manutenzione cade prima della scadenza del contratto (+1 mese)
  const map_park_list = computed(() => map_park_rows.value
    .filter(is_maintained_park)
    .map(to_maintained_park)
    .filter(p => {
      const exp_date = Date.parse(p.contract_validity_to);
      return (Date.parse(p.next_maint_date) < exp_date + monthMs + (p.self_renewable ? yearMs : 0));
    }));

  // rimuovo da subito i ticket che non hanno una descrizione
  const ticket_list = computed(() =>
    unsolved_tickets.value.filter(t => t.how_to_solve_descr.trim().length > 0)
  );

  const agency_by_id = computed(() => new Map(agency_list.value.map(agency => [agency.id, agency])));

  const marker_agency_ids = computed(() => {
    const preemptive_agencies = map_park_list.value.map(park => park.agency);
    const issue_agencies = ticket_list.value
      .filter(ticket => map_parks_by_id.value.has(ticket.park.id))
      .map(ticket => ticket.park.agency);
    return new Set([...preemptive_agencies, ...issue_agencies].map(agency_id => marker_agency(agency_id).id));
  });

  const agency_filter_options = computed(() => [
    ...agency_list.value,
    ...[NOT_ASSIGNED_AGENCY, UNKNOWN_AGENCY].filter(agency => marker_agency_ids.value.has(agency.id))
  ]);

  function marker_agency(agency_id: number | null): AgencyType {
    if (agency_id === null) {
      return NOT_ASSIGNED_AGENCY;
    }
    return agency_by_id.value.get(agency_id) ?? UNKNOWN_AGENCY;
  }

  const filtered_prio_set = computed(() => new Set(filtered_prio.value));
  const filtered_agencies_set = computed(() => new Set(filtered_agencies.value));
  const filtered_preemptives_set = computed(() => new Set(filtered_preemptives.value));
  const filtered_parks_set = computed(() => new Set(filtered_parks.value));

  function warn_parks_without_location() {
    const issue_park_ids = new Set(ticket_list.value.map(ticket => ticket.park.id));
    const parks_without_location = [...issue_park_ids].filter(park_id => !map_parks_by_id.value.has(park_id));
    if (parks_without_location.length > 0) {
      console.warn('Issue markers skipped, parks missing from map/parks:', parks_without_location);
    }
  }

  async function retrieveData() {
    loading_data.value = true;
    try {
      const [parks_result, agencies_result, tickets_result] = await Promise.allSettled([
        fetchMapParks(),
        fetchAgencies(),
        fetchUnsolvedTickets()
      ]);
      const errors: Array<string> = [];
      if (parks_result.status === 'fulfilled') {
        map_park_rows.value = parks_result.value;
      } else {
        console.log(parks_result.reason);
        errors.push(`Error loading map parks: ${parks_result.reason?.message}`);
      }
      if (agencies_result.status === 'fulfilled') {
        agency_list.value = agencies_result.value;
      } else {
        console.log(agencies_result.reason);
        errors.push(`Error loading agencies: ${agencies_result.reason?.message}`);
      }
      if (tickets_result.status === 'fulfilled') {
        unsolved_tickets.value = tickets_result.value;
      } else {
        console.log(tickets_result.reason);
        errors.push(`Error loading tickets: ${tickets_result.reason?.message}`);
      }
      if (parks_result.status === 'fulfilled' && tickets_result.status === 'fulfilled') {
        warn_parks_without_location();
      }
      if (errors.length > 0) {
        alert(errors.join('\n'));
      }
    } finally {
      loading_data.value = false;
    }
  }

  const displayed_tickets = computed(() => {
    let out = ticket_list.value;
    out = filtered_agencies_set.value.size > 0 ? out.filter(t => filtered_agencies_set.value.has(marker_agency(t.park.agency).id)) : out;
    out = filtered_prio_set.value.size > 0 ? out.filter(t => filtered_prio_set.value.has(t.priority)) : out;
    return out;
  });

  const markers_issues = computed(() => {
    return displayed_tickets.value.flatMap((ticket: IssueType) => {
      const map_park = map_parks_by_id.value.get(ticket.park.id);
      if (map_park === undefined) {
        return [];
      }
      const park = ticket.park;
      const agency = marker_agency(park.agency);
      return [{
        key: `issue-${ticket.id}`,
        color: issue_marker_color(ticket.priority),
        name: park.name,
        park: park.id,
        address: park.address + ', ' + park.city,
        agency_id: agency.id,
        date: ticket.start_date,
        agency: agency.name,
        location: { lat: parseFloat(map_park.latitude), lng: parseFloat(map_park.longitude) },
        issue_id: ticket.id,
        issue_title: ticket.title,
        made_by: ticket.made_by,
        priority: ticket.priority,
        cabinet_key: map_park.electrical_cabinet_with_key,
        cabinet_position: map_park.electrical_cabinet_where,
        kind: MaintenanceKind.issue,
        issue_how_to_solve: ticket.how_to_solve_descr,
      } as IssueMarker];
    });
  });

  const markers_maintenances: Ref<Array<PreemptiveMaintenanceMarker>> = computed(() => {
    return map_park_list.value.map(p => {
      const agency = marker_agency(p.agency);
      return {
        key: `preemptive-${p.id}`,
        color: preemptive_marker_color(p.next_maint_date),
        name: p.name,
        park: p.id,
        agency_id: agency.id,
        address: p.address + ', ' + p.city,
        date: p.next_maint_date,
        agency: agency.name,
        location: { lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) },
        maintenance_subscription: p.contract_maintenance_amount,
        contract_type: p.contract_type,
        cabinet_key: p.cabinet_key,
        cabinet_position: p.cabinet_position,
        kind: MaintenanceKind.preemptive,
        how_approach_client: p.how_approach_client,
        number_of_cs: p.number_of_cs,
        last_preventive_date: p.latest_preventive_maintenance_date
      } as PreemptiveMaintenanceMarker;
    }).toSorted(compare_markers);
  });

  const sorted_issues = computed(() => {
    let out: Array<IssueMarker> = [];
    if (show_issues.value) {
      out = markers_issues.value.toSorted(compare_issues);
      out = filtered_parks_set.value.size > 0 ? out.filter(m => filtered_parks_set.value.has(m.park)) : out;
    }
    return out;
  });

  const sorted_preemptive = computed(() => {
    let out: Array<PreemptiveMaintenanceMarker> = [];
    if (show_preemptive.value) {
      out = markers_maintenances.value;
      out = filtered_parks_set.value.size > 0 ? out.filter(m => filtered_parks_set.value.has(m.park)) : out;
      out = filtered_agencies_set.value.size > 0 ? out.filter(t => filtered_agencies_set.value.has(t.agency_id)) : out;
      out = filtered_preemptives_set.value.size > 0 ? out.filter(m => filtered_preemptives_set.value.has(m.color)) : out;
      return out;
    } else {
      return out;
    }
  });

  const maintenance_park_names = computed(() => {
    const names_by_id = new Map<number, string>();
    [...markers_maintenances.value, ...markers_issues.value].forEach(m => {
      if (!names_by_id.has(m.park)) {
        names_by_id.set(m.park, m.name);
      }
    });
    return Array.from(names_by_id, ([id, name]) => ({ id, name }));
  });

  const all_markers: Ref<Array<PreemptiveMaintenanceMarker | IssueMarker>> = computed(() =>
    [...sorted_preemptive.value, ...sorted_issues.value]
  );

  return {
    show_preemptive, show_issues, agency_filter_options, filtered_agencies, filtered_preemptives, filtered_parks,
    filtered_prio, sorted_preemptive, sorted_issues, all_markers, maintenance_park_names, preemptive_colors,
    priorities, loading_data, retrieveData
  };
}
